import React, { useState, useEffect } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const CartDrawer = ({ 
  isOpen, onClose, cart, setCart, removeFromCart, 
  customer, setCustomer, token, setToken, handleSendOrder, theme 
}) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState([]);
  const [isLoadingCP, setIsLoadingCP] = useState(false);
  const [availableColonies, setAvailableColonies] = useState([]);
  const [allMunicipiosData, setAllMunicipiosData] = useState(null);
  const [availableMunicipios, setAvailableMunicipios] = useState([]);

  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // 1. Cargar catálogo nacional
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/martinciscap/json-estados-municipios-mexico/master/estados-municipios.json')
      .then(res => res.json())
      .then(data => setAllMunicipiosData(data))
      .catch(err => console.error("Error cargando municipios:", err));
  }, []);

  // Limpieza al cerrar
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (!isOpen) { 
      setStep(1); 
      setToken(null); 
      setErrors([]); 
      setAvailableColonies([]); 
      setAvailableMunicipios([]);
    }
  }, [isOpen, setToken]);

  // Inicializar tipo de entrega
  useEffect(() => {
    if (isOpen && !customer.deliveryType) {
      setCustomer(prev => ({ ...prev, deliveryType: 'home', pais: 'México' }));
    }
  }, [isOpen, customer.deliveryType, setCustomer]);

  // 2. Lógica de Municipios
  useEffect(() => {
    if (customer.estado && allMunicipiosData) {
      const normalize = (str) => 
        str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : "";
      
      let estadoNorm = normalize(customer.estado);
      if (estadoNorm.includes('mexico') && !estadoNorm.includes('ciudad')) estadoNorm = 'mexico';
      if (estadoNorm.includes('distrito federal') || estadoNorm.includes('ciudad de mexico')) estadoNorm = 'ciudad de mexico';

      const stateKey = Object.keys(allMunicipiosData).find(key => normalize(key) === estadoNorm);
      
      if (stateKey) {
        setAvailableMunicipios(allMunicipiosData[stateKey]);
        if (customer.shippingZone === 'local') {
          setCustomer(prev => ({ ...prev, municipio: 'Zacoalco de Torres' }));
        }
      }
    }
  }, [customer.estado, customer.shippingZone, allMunicipiosData, setCustomer]);

  if (!isOpen) return null;

  const getPrice = (p) => typeof p === 'number' ? p : parseFloat(p.toString().replace(/[^0-9.]/g, '')) || 0;
  const total = cart.reduce((acc, item) => acc + (getPrice(item.price) * (item.qty || 1)), 0);

  const updateQty = (id, delta) => {
    setCart(cart.map(i => i.cartId === id ? { ...i, qty: Math.max(1, (i.qty || 1) + delta) } : i));
  };

  // VACIAR CARRITO
  const handleClearCart = () => {
    if(window.confirm("¿Estás seguro de que quieres vaciar todo tu carrito? 🐝")) {
      setCart([]);
    }
  };

  const handleCPChange = async (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setCustomer(prev => ({ ...prev, cp: val, colonia: '', estado: '', municipio: prev.shippingZone === 'local' ? 'Zacoalco de Torres' : '' }));
    
    if (val.length === 5) {
      setIsLoadingCP(true);
      try {
        const response = await fetch(`https://api.zippopotam.us/mx/${val}`);
        if (response.ok) {
          const data = await response.json();
          const coloniasList = data.places.map(p => p['place name']);
          setAvailableColonies(coloniasList);
          setCustomer(prev => ({
            ...prev,
            estado: data.places[0].state,
            colonia: coloniasList.length === 1 ? coloniasList[0] : ''
          }));
        }
      } finally { setIsLoadingCP(false); }
    }
  };

  const validate = () => {
    const errs = [];
    if (!customer.name?.trim()) errs.push("Nombre");
    if (!customer.lastName?.trim()) errs.push("Apellidos");
    
    if (customer.deliveryType === 'home') {
      if (!customer.shippingZone) errs.push("Zona de envío");
      if (!customer.calle?.trim()) errs.push("Calle");
      if (!customer.numExt?.trim()) errs.push("Núm Exterior");
      if (!customer.cp?.trim() || customer.cp.length < 5) errs.push("CP");
      if (!customer.colonia?.trim()) errs.push("Colonia");
      if (!customer.municipio?.trim()) errs.push("Municipio");
    } else {
      if (!customer.pickupDate) errs.push("Fecha");
      if (!customer.pickupTime) errs.push("Hora");
    }
    
    if (!token) errs.push("Captcha");
    setErrors(errs);
    if (errs.length === 0) handleSendOrder();
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#FFFBF0] dark:bg-[#1C1B17] h-full shadow-2xl flex flex-col border-l-4 border-yellow-500 transition-colors">
        
        {/* HEADER */}
        <div className="p-4 border-b border-yellow-200 dark:border-[#594400]">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl font-black text-[#594400] dark:text-[#FFD54F] uppercase italic">
              {step === 1 ? "Mi Pedido" : "Datos de Entrega"}
            </h2>
            <button onClick={onClose} className="text-3xl text-yellow-900 dark:text-[#FFD54F]/50">&times;</button>
          </div>
        </div>

        {/* CONTENIDO */}
        <div className="flex-grow overflow-y-auto p-4 custom-scroll">
          {step === 1 ? (
            <div className="space-y-4">
              {cart.length > 0 && (
                <div className="flex justify-end">
                  <button onClick={handleClearCart} className="text-[10px] font-black text-red-500 hover:text-red-700 uppercase border-b border-red-200">
                    🗑️ Vaciar todo el carrito
                  </button>
                </div>
              )}

              {cart.map((item) => (
                <div key={item.cartId} className="bg-white dark:bg-[#2A2821] p-3 rounded-2xl border-2 border-yellow-100 dark:border-[#594400] flex gap-3 items-center">
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <p className="font-black text-[#594400] dark:text-[#FFD54F] uppercase text-[10px] pr-2">{item.name}</p>
                      <button onClick={() => removeFromCart(item.cartId)} className="text-red-500 font-bold text-lg leading-none">&times;</button>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <p className="font-black text-yellow-600">${(getPrice(item.price) * item.qty).toFixed(2)}</p>
                      <div className="flex items-center bg-yellow-50 dark:bg-black/40 rounded-lg">
                        <button onClick={() => updateQty(item.cartId, -1)} className="w-7 h-7 font-black text-[#594400]">-</button>
                        <span className="px-2 font-black text-xs text-[#594400] dark:text-white">{item.qty}</span>
                        <button onClick={() => updateQty(item.cartId, 1)} className="w-7 h-7 font-black text-[#594400]">+</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {cart.length === 0 && <div className="text-center py-10 text-yellow-800 font-bold">🐝 Tu panal está vacío.</div>}
            </div>
          ) : (
            <div className="space-y-4">
              <button onClick={() => setStep(1)} className="text-[10px] font-black uppercase text-yellow-700 underline decoration-2">⬅ Volver al carrito</button>
              
              <div className="flex bg-yellow-100 dark:bg-[#2A2821] p-1 rounded-xl shadow-inner border border-yellow-200 dark:border-[#594400]">
                <button className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${customer.deliveryType === 'pickup' ? 'bg-white dark:bg-[#594400] text-[#594400] dark:text-[#FFD54F]' : 'text-yellow-700'}`} onClick={() => setCustomer({ ...customer, deliveryType: 'pickup', shippingZone: null })}>🏪 Recoger</button>
                <button className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${customer.deliveryType === 'home' ? 'bg-white dark:bg-[#594400] text-[#594400] dark:text-[#FFD54F]' : 'text-yellow-700'}`} onClick={() => setCustomer({ ...customer, deliveryType: 'home' })}>🚚 Domicilio</button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Nombre*" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] dark:text-white outline-none" value={customer.name || ''} onChange={e => setCustomer({...customer, name: e.target.value})} />
                <input type="text" placeholder="Apellidos*" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] dark:text-white outline-none" value={customer.lastName || ''} onChange={e => setCustomer({...customer, lastName: e.target.value})} />
              </div>

              {customer.deliveryType === 'home' && (
                <div className="space-y-4 animate-fade-up border-t border-yellow-200 pt-4 mt-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button className={`py-2 px-2 rounded-lg text-xs font-bold border ${customer.shippingZone === 'local' ? 'bg-[#F9A825] text-white' : 'bg-white dark:bg-[#2A2821]'}`} onClick={() => setCustomer({ ...customer, shippingZone: 'local', municipio: 'Zacoalco de Torres', estado: 'Jalisco' })}>📍 Local</button>
                    <button className={`py-2 px-2 rounded-lg text-xs font-bold border ${customer.shippingZone === 'foraneo' ? 'bg-[#594400] text-white' : 'bg-white dark:bg-[#2A2821]'}`} onClick={() => setCustomer({ ...customer, shippingZone: 'foraneo', municipio: '', estado: '' })}>✈️ Foráneo</button>
                  </div>

                  {customer.shippingZone === 'foraneo' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl animate-pulse">
                      <p className="text-[10px] leading-tight text-amber-800 font-bold uppercase">⚠️ ENVÍO (FEDEX/DHL) ES EXTRA. SE COTIZA POR WHATSAPP.</p>
                    </div>
                  )}

                  {customer.shippingZone && (
                    <div className="space-y-3">
                      <input type="text" placeholder="Calle*" className="w-full p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.calle || ''} onChange={e => setCustomer({...customer, calle: e.target.value})} />
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" placeholder="Num Ext*" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.numExt || ''} onChange={e => setCustomer({...customer, numExt: e.target.value})} />
                        <input type="text" placeholder="Num Int" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.numInt || ''} onChange={e => setCustomer({...customer, numInt: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                          <input type="text" placeholder="CP*" maxLength="5" className="w-full p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.cp || ''} onChange={handleCPChange} />
                          {isLoadingCP && <span className="absolute right-3 top-3 text-yellow-500 text-xs font-bold animate-pulse">...</span>}
                        </div>
                        <input type="text" readOnly className="p-3 rounded-lg border border-yellow-200 bg-gray-100 text-gray-500" value={customer.estado || ''} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {availableColonies.length > 1 ? (
                          <select className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.colonia || ''} onChange={e => setCustomer({...customer, colonia: e.target.value})}>
                            <option value="">Colonia*</option>
                            {availableColonies.map(col => (<option key={col} value={col}>{col}</option>))}
                          </select>
                        ) : (
                          <input type="text" placeholder="Colonia*" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] outline-none" value={customer.colonia || ''} onChange={e => setCustomer({...customer, colonia: e.target.value})} />
                        )}
                        <input type="text" readOnly className="p-3 rounded-lg border border-yellow-200 bg-gray-100 text-gray-500" value={customer.municipio || ''} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {customer.deliveryType === 'pickup' && (
                <div className="space-y-4 pt-4 border-t border-yellow-200">
                  <div className="bg-green-50 border border-green-200 p-3 rounded-xl text-center italic text-xs text-green-800 font-medium">📍 Cruz Verde, Zacoalco de Torres.</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="date" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] text-sm outline-none" value={customer.pickupDate || ''} min={new Date().toISOString().split('T')[0]} onChange={e => setCustomer({...customer, pickupDate: e.target.value})} />
                    <input type="time" className="p-3 rounded-lg border border-yellow-200 bg-white dark:bg-[#2A2821] text-sm outline-none" value={customer.pickupTime || ''} onChange={e => setCustomer({...customer, pickupTime: e.target.value})} />
                  </div>
                </div>
              )}

              {/* APARTADO DE NOTAS / PREGUNTAS */}
              <div className="pt-4 border-t border-yellow-200">
                <label className="text-[10px] font-black text-[#594400] dark:text-[#FFD54F] uppercase ml-1">¿Alguna nota o pregunta para nosotros?</label>
                <textarea 
                  placeholder="Ej: Tocar fuerte el timbre, preguntar por el polen de temporada..." 
                  className="w-full p-3 mt-1 rounded-xl border-2 border-yellow-100 dark:border-[#594400] bg-white dark:bg-[#2A2821] dark:text-white outline-none text-xs min-h-[80px] resize-none focus:border-yellow-400"
                  value={customer.notes || ''}
                  onChange={e => setCustomer({...customer, notes: e.target.value})}
                />
              </div>

              <div className="flex justify-center pt-2">
                <Turnstile siteKey={SITE_KEY} onSuccess={(t) => setToken(t)} options={{ theme: theme === 'dark' ? 'dark' : 'light' }} />
              </div>

              {errors.length > 0 && <div className="text-red-600 text-[10px] font-bold p-2 bg-red-50 border border-red-200 rounded-lg text-center uppercase">Falta: {errors.join(', ')}</div>}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t border-yellow-200 dark:border-[#594400] bg-white/50 dark:bg-black/20">
          <div className="flex justify-between items-end mb-4 font-black">
            <span className="text-[10px] uppercase text-yellow-800">Total</span>
            <span className="text-3xl text-[#594400] dark:text-white">${total.toFixed(2)}</span>
          </div>
          {step === 1 ? (
            <button onClick={() => { if(cart.length > 0) setStep(2); }} className={`w-full py-4 rounded-xl font-black uppercase ${cart.length > 0 ? 'bg-[#F9A825] text-white' : 'bg-gray-300 text-gray-500'}`}>Siguiente ⮕</button>
          ) : (
            <button onClick={validate} className={`w-full py-4 bg-[#25D366] text-white rounded-xl font-black flex justify-center gap-2 ${!token ? 'opacity-50' : ''}`}>ENVIAR WHATSAPP</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;